import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import moment from 'moment';
import pluralize from 'pluralize';

import ToE164Phone from 'common/util/ToE164Phone';
import { bedBathsDisplayString, formatPhoneNumber, formatPriceRange } from 'common/formatters';

import { SR_API_ACTION_AGENT_SUBMIT_TOUR_REPORT } from 'api/actionTypes';
import { TOUR_SHOWING_STATUS_COMPLETED, TOUR_SHOWING_STATUS_FAILED } from 'common/tourConstants';

import { leaseSituationShortOptionItems } from 'common/leaseSituationOptionItems';

import SrAgentToursRowListing from 'client/components/SrAgentToursRowListing';

import withApiState from 'client/helpers/withApiState';

const activeShowingStatuses = [TOUR_SHOWING_STATUS_COMPLETED, TOUR_SHOWING_STATUS_FAILED];

function formatArrayToString(array, emptyArrayString = 'None') {
  return array ? array.join(', ') : emptyArrayString;
}

@withApiState(({ tour }) => {
  return {
    agentSubmitTourReportApiState: {
      action: SR_API_ACTION_AGENT_SUBMIT_TOUR_REPORT,
      queryId: `${tour.id}`,
    },
  };
})
class SrAgentToursRow extends Component {
  static propTypes = {
    tour: PropTypes.object.isRequired,
    notes: PropTypes.string.isRequired,
    renterName: PropTypes.string.isRequired,
    listingQuestions: PropTypes.object.isRequired,
    expanded: PropTypes.bool.isRequired,
    agentUserId: PropTypes.number.isRequired,
    onToggleTourExpandedView: PropTypes.func.isRequired,
    onTourValueChange: PropTypes.func.isRequired,
    onSubmitReport: PropTypes.func.isRequired,
    agentSubmitTourReportApiState: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    userAgent: PropTypes.object.isRequired,
    listingPhotoResolution: PropTypes.number.isRequired,
  };

  render() {
    const {
      tour,
      notes,
      renterName,
      listingQuestions,
      agentUserId,
      expanded,
      onToggleTourExpandedView,
      onTourValueChange,
      onSubmitReport,
      agentSubmitTourReportApiState,
      history,
      userAgent,
      listingPhotoResolution,
    } = this.props;

    const user = tour.users[0];

    let bodyDOM = null;
    if (expanded) {
      const completedShowings = `${tour.userCompletedShowingsCount} ${pluralize(
        'rental',
        tour.userCompletedShowingsCount,
      )}`;

      const listings = [...tour.listings].sort((listingA, listingB) => {
        const showingA = tour.showings.find(({ listingId }) => listingId === listingA.id);
        const showingB = tour.showings.find(({ listingId }) => listingId === listingB.id);

        if (showingA.note) {
          return 1;
        }
        if (showingB.note) {
          return -1;
        }

        return showingA.position - showingB.position;
      });

      const isTourCompleted = tour.showings.every((showing) =>
        activeShowingStatuses.includes(showing.status),
      );

      const leaseSituationOptionItem = leaseSituationShortOptionItems.find(({ value }) => {
        return value === user.leaseSituation;
      });

      const leaseSituationLabel = (leaseSituationOptionItem || {}).label || null;

      bodyDOM = (
        <div>
          <div className="renterHeaderContainer">
            <div className="phoneNumber bold">{formatPhoneNumber(user.phone)}</div>
            <div className="contactButtonsContainer">
              <a href={`sms:${ToE164Phone(user.phone)}`} className="smallWhiteButtonIcon text">
                text
              </a>
              <a href={`tel:${ToE164Phone(user.phone)}`} className="smallWhiteButtonIcon call">
                call
              </a>
            </div>
          </div>

          <div className="sectionContainer renterInfoContainer">
            <div className="section">
              <span className="headline">history</span>
              <div className="sectionContent renterInfoList">
                <span className="listItem">
                  Signed up {moment(user.createdDate).format('MMM D, YYYY')}
                </span>
                <span className="listItem">
                  <span className="bold">{completedShowings} toured</span> so far with Sunroom
                </span>
                <span className="listItem">
                  <span>Current Lease Situation: {leaseSituationLabel}</span>
                </span>
              </div>
            </div>

            <div className="section">
              <span className="headline">search filters</span>
              <div className="sectionContent renterInfoList">
                <span className="listItem">
                  {`Property Type: ${formatArrayToString(user.searchFilters.propertyTypes)}`}
                </span>
                <span className="listItem">
                  <span>{bedBathsDisplayString(user.searchFilters)}</span>
                </span>
                <span className="listItem">
                  {`Property Price: ${formatPriceRange(user.searchFilters)}`}
                </span>
                <span className="listItem">
                  {`Pets: ${formatArrayToString(user.searchFilters.pets, 'No Pets')}`}
                </span>
                <span className="listItem">
                  {`Unit Amenities: ${formatArrayToString(user.searchFilters.unitAmenities)}`}
                </span>
                <span className="listItem">
                  {`Move-in Date: ${moment(user.searchFilters.moveInDate || user.moveInDate).format(
                    'MMM D, YYYY',
                  )}`}
                </span>
                <span className="listItem">
                  {user.searchFilters.section8
                    ? 'Section 8 / Housing Voucher Accepted'
                    : 'No Section 8 / Housing Voucher'}
                </span>
              </div>
            </div>
          </div>

          <div className="sectionContainer nameContainer">
            <div className="section">
              <span className="headline">renter's full name</span>
              <div className="sectionContent textInput">
                <input
                  className="sr-input"
                  value={renterName}
                  onChange={(event) =>
                    onTourValueChange(tour.id, {
                      renterName: event.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>

          <div className="sectionContainer showingsList">
            {listings.map((listing) => {
              const showing = tour.showings.find((showing) => showing.listingId === listing.id);
              return (
                <SrAgentToursRowListing
                  key={listing.id}
                  listing={listing}
                  showing={showing}
                  agentUserId={agentUserId}
                  tourId={tour.id}
                  listingQuestions={listingQuestions[listing.id] || ['']}
                  history={history}
                  userAgent={userAgent}
                  listingPhotoResolution={listingPhotoResolution}
                />
              );
            })}
          </div>

          <div className="sectionContainer notesContainer all">
            <div className="section">
              <span className="headline">general notes on the tour</span>
              <div className="sectionContent textInput">
                <textarea
                  className="sr-textarea tourNotesTextarea"
                  placeholder="Write general tour notes..."
                  value={notes}
                  onChange={(event) =>
                    onTourValueChange(tour.id, {
                      notes: event.target.value,
                    })
                  }
                />
                <button
                  className="button yellow"
                  type="button"
                  onClick={() => onSubmitReport(tour.id)}
                  disabled={
                    agentSubmitTourReportApiState.inProgress ||
                    !notes ||
                    notes === tour.notes ||
                    !isTourCompleted
                  }
                >
                  {agentSubmitTourReportApiState.inProgress
                    ? 'submitting...'
                    : !tour.notes
                    ? 'submit report'
                    : 'update report'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={classnames('tourContainer', {
          tourReportNotSubmitted: !tour.notes,
        })}
      >
        <div className="header" onClick={() => onToggleTourExpandedView(tour.id)}>
          <div className="name">{tour.users[0].name}</div>
          <div className="time">{moment(tour.startDate).format('h:mmA')}</div>
        </div>
        {bodyDOM}
      </div>
    );
  }
}

SrAgentToursRow.displayName = 'SrAgentToursRow';

export default SrAgentToursRow;
